import React, { useEffect, useState } from "react";
import "../../Styles/UASavedContent.css";  
import "../../Styles/UAArchiveContent.css"
import "../../../Styles/common-styles.css";
import fetchArchiveData from "./UAFetchArchiveData"

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_MIN_MS = 60 * 1000;

const UAArchiveContent = () => {
  const [archiveData, setArchiveData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArchiveData().then((data) => {
      setArchiveData(data);  // This should follow ArchiveStructure
      setLoading(false);
    });
  }, [fetchArchiveData]);

  if (loading) {
    return <div className="text-header">Loading archived posts...</div>;
  }

  if (!archiveData || !archiveData.profiles) {
    return <div className="text-label">No archived posts available.</div>;
  }

/**
 * Try to parse a date string into a Date object.
 * Supports:
 *  - Date instance or numeric timestamp
 *  - ISO strings like "2025-03-17T19:30:00Z" or "2025-03-17T19:30:00"
 *  - Locale-like "17/03/2025" or "17/03/2025, 19:30:00" (assumed DD/MM/YYYY)
 *
 * Returns null if parsing fails.
 */
function parseDateOrNull(input) {
  if (!input && input !== 0) return null;

  if (input instanceof Date) {
    if (isNaN(input.getTime())) return null;
    return input;
  }

  if (typeof input === "number") {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof input !== "string") return null;

  const raw = input.trim();

  // 1) Try native parsing first (handles ISO and many other formats)
  const native = Date.parse(raw);
  if (!Number.isNaN(native)) return new Date(native);

  // 2) Try to parse "DD/MM/YYYY" or "DD/MM/YYYY, HH:MM:SS"
  //    e.g. "17/03/2025, 19:30:00" or "17/3/2025 19:30"
  const parts = raw.split(/[\s,]+/); // splits "date" and "time" if comma/space separated
  const datePart = parts[0];
  const timePart = parts[1] || "00:00:00";

  if (datePart.includes("/")) {
    const [p1, p2, p3] = datePart.split("/");
    // assume DD/MM/YYYY if the third part is 4 digits
    if (p1 && p2 && p3 && p3.length === 4) {
      const dd = p1.padStart(2, "0");
      const mm = p2.padStart(2, "0");
      const yyyy = p3;
      // build an ISO-like string (local time)
      const isoLike = `${yyyy}-${mm}-${dd}T${timePart}`;
      const parsed = Date.parse(isoLike);
      if (!Number.isNaN(parsed)) return new Date(parsed);
    }
  }

  // 3) fallback: invalid
  return null;
}

/**
 * Return remaining time components (never negative).
 * { msRemaining, days, hours, minutes, expired }
 */
function getTimeRemaining(archivedTill) {
  const tillDate = parseDateOrNull(archivedTill);
  if (!tillDate) {
    return {
      msRemaining: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      expired: true,
    };
  }

  const msRemaining = tillDate.getTime() - Date.now();
  const expired = msRemaining <= 0;
  const absMs = Math.max(msRemaining, 0);

  const days = Math.floor(absMs / ONE_DAY_MS);
  const hours = Math.floor((absMs % ONE_DAY_MS) / ONE_HOUR_MS);
  const minutes = Math.floor((absMs % ONE_HOUR_MS) / ONE_MIN_MS);

  return { msRemaining, days, hours, minutes, expired };
}

/**
 * Business rule for color:
 *  - if expired => "gray" (you can change to "red" if you want expired to be urgent)
 *  - if <= 1 day left => "red"
 *  - otherwise => "green"
 */
function getStatusColor(archivedTill) {
  const { expired, days } = getTimeRemaining(archivedTill);

  if (expired) return "gray";    // archived/till passed
  if (days < 1) return "red";    // less than 1 day left
  return "green";
}


  return (
    <div>
      {archiveData.profiles.map((profile) => (
        <div key={profile.profileId} style={{ marginBottom: "40px" }}>
          <h2 className="text-header" style={{ marginLeft: 15 }}>
            Profile: {profile.profileId}
          </h2>
          <p className="text-label" style={{ marginLeft: 15, marginBottom: 10 }}>
            Archived On: {new Date(profile.archivedOn).toLocaleString()} <br />
            Archived Till: {new Date(profile.archivedTill).toLocaleString()} <br />
            Size Consumed: {profile.sizeConsumed} MB
          </p>
  
          <div
            className="archive-feed-container"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
              gap: "20px",
            }}
          >
            {profile.posts.map((post) => (
              <div
                key={post.postId}
                className="saved-feed-card"
                style={{
                  border: "1px solid var(--border-color, rgb(52, 51, 51))",
                  padding: "10px",
                  borderRadius: "10px",
                  backgroundColor: "none",
                  width: "100%"
                }}
              >
                {/* Media Grid */}
                <div
                  className="saved-feed-media"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    justifyContent: "flex-start",
                  }}
                >
                  {post.medias.map((media, index) => (
                    <img
                      key={index}
                      src={media.mediaurl}
                      alt="Archived Media"
                      className="saved-media-img"
                      style={{
                        flex: "1 1 100px",
                        maxWidth: "calc(33% - 10px)", // up to 3 per row
                        height: "120px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                  ))}
                </div>
  
                {/* Post Info */}
                <div className="text-label" style={{ marginTop: "10px" }}>
                  <p>
                    <strong>Created:</strong>{" "}
                    {new Date(post.timestamp).toLocaleString()}
                  </p>
                  <p>
                    <strong>Archived Till:</strong>{" "}
                    {new Date(profile.archivedTill).toLocaleString()}
                  </p>
                  <p>
                    <strong>Size:</strong> {profile.sizeConsumed} MB
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );  
};

export default UAArchiveContent;
